import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  BadgeCheck,
  CalendarClock,
  FileDown,
  FileText,
  History,
  ScanLine,
  ShieldX,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import { api, type Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Modal, Campo, claseEntrada } from '@/shared/ui/Modal';
import { Pestanas, type Pestana } from '@/shared/ui/Pestanas';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { VistaPreviaDeImpresion } from '@/shared/ui/VistaPreviaDeImpresion';
import { notificar } from '@/shared/ui/avisos';
import { useValorDiferido } from '@/shared/ui/useValorDiferido';
import { useSesion } from '@/shared/sesion/SesionProvider';
import type {
  CertificadoEmitido,
  DetalleDeCertificado,
  EstadoDelCertificado,
  RechazoDeCertificado,
  ResumenDeCertificados,
} from './contrato';

type Vista = 'emitidos' | 'rechazos';

const PESTANAS: ReadonlyArray<Pestana<Vista>> = [
  {
    clave: 'emitidos',
    rotulo: 'Emitidos',
    descripcion: 'Lo que sacaron los ciudadanos',
    icono: BadgeCheck,
  },
  { clave: 'rechazos', rotulo: 'Rechazados', descripcion: 'Lo pidieron y debían', icono: ShieldX },
];

const ESTADOS: Record<EstadoDelCertificado, string> = {
  VIGENTE: 'bg-marca-50 text-marca-700 ring-marca-200',
  VENCIDO: 'bg-armazon-100 text-armazon-500 ring-armazon-200',
  ANULADO: 'bg-red-50 text-alerta ring-red-200',
};

const dinero = new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' });

function fechaYHora(instante: string): string {
  return new Date(instante).toLocaleString('es-EC', {
    timeZone: 'America/Guayaquil',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fecha(dia: string): string {
  const [a, m, d] = dia.split('-');
  return `${d}/${m}/${a}`;
}

export function PaginaCertificados() {
  const cliente = useQueryClient();
  const { puede } = useSesion();
  const [vista, setVista] = useState<Vista>('emitidos');
  const [buscar, setBuscar] = useState('');
  const [estado, setEstado] = useState<'' | EstadoDelCertificado>('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [detalle, setDetalle] = useState<CertificadoEmitido | null>(null);
  const [anulando, setAnulando] = useState<CertificadoEmitido | null>(null);
  const [pdf, setPdf] = useState<{ certificado: CertificadoEmitido; fichero: Blob | null } | null>(
    null,
  );
  const [bajando, setBajando] = useState(false);

  const texto = useValorDiferido(buscar.trim());

  function parametros(conEstado: boolean): string {
    const p = new URLSearchParams();
    if (desde) p.set('desde', desde);
    if (hasta) p.set('hasta', hasta);
    if (conEstado && estado) p.set('estado', estado);
    if (texto) p.set('q', texto);
    return p.toString();
  }

  const resumen = useQuery({
    queryKey: ['certificados', 'resumen'],
    queryFn: () => api.get<ResumenDeCertificados>('/certificados-no-adeudar/resumen'),
  });

  const emitidos = useQuery({
    queryKey: ['certificados', 'emitidos', desde, hasta, estado, texto, pagina, tamanio],
    enabled: vista === 'emitidos',
    queryFn: () =>
      api.get<Pagina<CertificadoEmitido>>(
        `/certificados-no-adeudar?${parametros(true)}&pagina=${pagina}&tamanio=${tamanio}`,
      ),
  });

  const rechazos = useQuery({
    queryKey: ['certificados', 'rechazos', desde, hasta, texto, pagina, tamanio],
    enabled: vista === 'rechazos',
    queryFn: () =>
      api.get<Pagina<RechazoDeCertificado>>(
        `/certificados-no-adeudar/rechazos?${parametros(false)}&pagina=${pagina}&tamanio=${tamanio}`,
      ),
  });

  async function verPdf(c: CertificadoEmitido) {
    setPdf({ certificado: c, fichero: null });
    try {
      const fichero = await api.descargar(`/certificados-no-adeudar/${c.id}/pdf`);
      setPdf((actual) =>
        actual && actual.certificado.id === c.id ? { ...actual, fichero } : actual,
      );
    } catch (causa) {
      setPdf(null);
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo abrir el PDF');
    }
  }

  async function descargarHoja() {
    setBajando(true);
    try {
      const fichero = await api.descargar(
        `/certificados-no-adeudar/reporte.xlsx?${parametros(true)}`,
      );
      if (!fichero) {
        notificar.error('NO SE PUDO GENERAR LA HOJA');
        return;
      }
      const url = URL.createObjectURL(fichero);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = `certificados-no-adeudar-${new Date().toISOString().slice(0, 10)}.xlsx`;
      enlace.click();
      URL.revokeObjectURL(url);
    } catch (causa) {
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo generar la hoja');
    } finally {
      setBajando(false);
    }
  }

  function cambiarFiltro(accion: () => void) {
    accion();
    setPagina(0);
  }

  const r = resumen.data;

  const columnasEmitidos: Columna<CertificadoEmitido>[] = [
    {
      clave: 'numero',
      encabezado: 'Certificado',
      clase: 'w-44',
      contenido: (c) => (
        <span className="block">
          <span className="importe block text-xs font-semibold text-armazon-900">{c.numero}</span>
          <span className="importe block text-[11px] text-armazon-400">{c.codigo}</span>
        </span>
      ),
    },
    {
      clave: 'contribuyente',
      encabezado: 'Contribuyente',
      contenido: (c) => (
        <span className="block min-w-0">
          <span className="block truncate text-xs font-semibold uppercase text-armazon-800">
            {c.contribuyente}
          </span>
          <span className="importe block text-[11px] text-armazon-500">{c.identificacion}</span>
        </span>
      ),
    },
    {
      clave: 'emitido',
      encabezado: 'Emitido',
      clase: 'w-40',
      contenido: (c) => (
        <span className="block text-xs text-armazon-600">
          <span className="block">{fechaYHora(c.emitidoEn)}</span>
          <span className="block text-[11px] uppercase text-armazon-400">
            Hasta {fecha(c.validoHasta)}
          </span>
        </span>
      ),
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      alinear: 'centro',
      clase: 'w-28',
      contenido: (c) => (
        <span
          className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ring-1 ${ESTADOS[c.estado]}`}
        >
          {c.estado}
        </span>
      ),
    },
    {
      clave: 'validaciones',
      encabezado: 'Validado',
      alinear: 'centro',
      clase: 'w-24',
      contenido: (c) => (
        <span
          className={`importe inline-flex items-center gap-1 text-xs ${c.validaciones > 0 ? 'font-semibold text-blue-700' : 'text-armazon-400'}`}
          title={
            c.ultimaValidacionEn
              ? `Última validación: ${fechaYHora(c.ultimaValidacionEn)}`
              : 'Nadie lo ha validado'
          }
        >
          <ScanLine className="size-3.5" aria-hidden />
          {c.validaciones}
        </span>
      ),
    },
    {
      clave: 'acciones',
      encabezado: 'Acciones',
      alinear: 'centro',
      clase: 'w-32 pr-5',
      contenido: (c) => (
        <div className="flex justify-center gap-1">
          <BotonDeFila
            icono={FileText}
            etiqueta={`Ver el PDF de ${c.numero}`}
            alPulsar={() => void verPdf(c)}
          />
          <BotonDeFila
            icono={History}
            etiqueta={`Detalle de ${c.numero}`}
            alPulsar={() => setDetalle(c)}
          />
          {puede('certificados.anular') && c.estado !== 'ANULADO' && (
            <BotonDeFila
              icono={Ban}
              etiqueta={`Anular ${c.numero}`}
              alPulsar={() => setAnulando(c)}
              peligro
            />
          )}
        </div>
      ),
    },
  ];

  const columnasRechazos: Columna<RechazoDeCertificado>[] = [
    {
      clave: 'intentado',
      encabezado: 'Pedido el',
      clase: 'w-40',
      contenido: (x) => (
        <span className="text-xs text-armazon-600">{fechaYHora(x.intentadoEn)}</span>
      ),
    },
    {
      clave: 'contribuyente',
      encabezado: 'Contribuyente',
      contenido: (x) => (
        <span className="block min-w-0">
          <span className="block truncate text-xs font-semibold uppercase text-armazon-800">
            {x.contribuyente ?? 'Sin nombre en el catastro'}
          </span>
          <span className="importe block text-[11px] text-armazon-500">{x.identificacion}</span>
        </span>
      ),
    },
    {
      clave: 'motivo',
      encabezado: 'Motivo',
      clase: 'w-36',
      contenido: (x) =>
        x.motivo === 'DEUDA' ? (
          <span className="inline-block rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800 ring-1 ring-amber-200">
            Con deuda
          </span>
        ) : (
          <span className="inline-block rounded-full bg-armazon-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-armazon-600 ring-1 ring-armazon-200">
            Sin catastro
          </span>
        ),
    },
    {
      clave: 'deuda',
      encabezado: 'Debía',
      alinear: 'derecha',
      clase: 'w-44 pr-5',
      contenido: (x) =>
        x.motivo === 'DEUDA' ? (
          <span className="block">
            <span className="importe block text-sm font-semibold text-armazon-900">
              {dinero.format(x.deudaTotal)}
            </span>
            <span className="block text-[11px] uppercase text-armazon-400">
              {x.facturas} {x.facturas === 1 ? 'factura' : 'facturas'}
              {x.titulos > 0
                ? ` · ${x.titulos} ${x.titulos === 1 ? 'título' : 'títulos'} Art. 8`
                : ''}
            </span>
          </span>
        ) : (
          <span className="text-xs text-armazon-400">—</span>
        ),
    },
  ];

  return (
    <>
      <EncabezadoDePagina
        icono={BadgeCheck}
        color="esmeralda"
        titulo="Certificados de no adeudar"
        subtitulo="Lo que emiten los ciudadanos en la ventanilla en línea"
        acciones={
          vista === 'emitidos' && (
            <button
              type="button"
              onClick={() => void descargarHoja()}
              disabled={bajando}
              className="flex items-center gap-2 rounded-xl border border-armazon-200 bg-white px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-armazon-700 shadow-sm transition-colors hover:border-marca-300 hover:text-marca-700 disabled:opacity-50"
            >
              <FileDown className="size-4" aria-hidden />
              {bajando ? 'Generando…' : 'Excel'}
            </button>
          )
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        <Cifra titulo="Hoy" valor={r?.emitidosHoy} icono={CalendarClock} />
        <Cifra titulo="Este mes" valor={r?.emitidosDelMes} icono={BadgeCheck} />
        <Cifra titulo="Vigentes" valor={r?.vigentes} icono={BadgeCheck} tono="marca" />
        <Cifra titulo="Vencidos" valor={r?.vencidos} icono={CalendarClock} />
        <Cifra titulo="Anulados" valor={r?.anulados} icono={Ban} tono="alerta" />
        <Cifra
          titulo="Rechazados este mes"
          valor={r?.rechazosDelMes}
          icono={TriangleAlert}
          tono="ambar"
        />
        <Cifra
          titulo="Validados este mes"
          valor={r?.validacionesDelMes}
          icono={ScanLine}
          tono="azul"
        />
      </div>

      <Pestanas
        pestanas={PESTANAS}
        activa={vista}
        alElegir={(v) => cambiarFiltro(() => setVista(v))}
        etiqueta="Certificados emitidos o rechazados"
      />

      <div className="mt-4">
        {vista === 'emitidos' ? (
          <Tabla
            datos={emitidos.data}
            cargando={emitidos.isPending}
            columnas={columnasEmitidos}
            tamanio={tamanio}
            alCambiarPagina={setPagina}
            alCambiarTamanio={(n) => cambiarFiltro(() => setTamanio(n))}
            iconoVacio={BadgeCheck}
            mensajeVacio="NINGÚN CERTIFICADO COINCIDE CON EL FILTRO"
            entidad="certificados"
            herramientas={
              <Filtros
                buscar={buscar}
                alBuscar={(v) => cambiarFiltro(() => setBuscar(v))}
                desde={desde}
                hasta={hasta}
                alCambiarDesde={(v) => cambiarFiltro(() => setDesde(v))}
                alCambiarHasta={(v) => cambiarFiltro(() => setHasta(v))}
                estado={estado}
                alCambiarEstado={(v) => cambiarFiltro(() => setEstado(v))}
              />
            }
          />
        ) : (
          <Tabla
            datos={rechazos.data}
            cargando={rechazos.isPending}
            columnas={columnasRechazos}
            tamanio={tamanio}
            alCambiarPagina={setPagina}
            alCambiarTamanio={(n) => cambiarFiltro(() => setTamanio(n))}
            iconoVacio={ShieldX}
            mensajeVacio="NADIE HA SIDO RECHAZADO CON ESE FILTRO"
            entidad="rechazos"
            herramientas={
              <Filtros
                buscar={buscar}
                alBuscar={(v) => cambiarFiltro(() => setBuscar(v))}
                desde={desde}
                hasta={hasta}
                alCambiarDesde={(v) => cambiarFiltro(() => setDesde(v))}
                alCambiarHasta={(v) => cambiarFiltro(() => setHasta(v))}
              />
            }
          />
        )}
      </div>

      {detalle && <DialogoDeDetalle certificado={detalle} alCerrar={() => setDetalle(null)} />}

      {anulando && (
        <DialogoDeAnulacion
          certificado={anulando}
          alCerrar={() => setAnulando(null)}
          alAnular={() => void cliente.invalidateQueries({ queryKey: ['certificados'] })}
        />
      )}

      <VistaPreviaDeImpresion
        abierto={pdf !== null}
        alCerrar={() => setPdf(null)}
        titulo={`Certificado ${pdf?.certificado.numero ?? ''}`}
        subtitulo={pdf ? `${pdf.certificado.contribuyente} · ${pdf.certificado.estado}` : undefined}
        fichero={pdf?.fichero ?? null}
        cargando={pdf !== null && pdf.fichero === null}
        textoDeCarga="Abriendo el certificado…"
        formato="hoja"
        conBarraDelVisor
      />
    </>
  );
}

function Filtros({
  buscar,
  alBuscar,
  desde,
  hasta,
  alCambiarDesde,
  alCambiarHasta,
  estado,
  alCambiarEstado,
}: {
  buscar: string;
  alBuscar: (v: string) => void;
  desde: string;
  hasta: string;
  alCambiarDesde: (v: string) => void;
  alCambiarHasta: (v: string) => void;
  estado?: '' | EstadoDelCertificado;
  alCambiarEstado?: (v: '' | EstadoDelCertificado) => void;
}) {
  const claseFecha =
    'h-10 rounded-lg border border-armazon-200 bg-white px-2 text-xs text-armazon-700 outline-none focus:border-marca-400';
  return (
    <div className="flex w-full flex-wrap items-center gap-2">
      <CampoDeBusqueda
        valor={buscar}
        alCambiar={alBuscar}
        marcador="Número, código, cédula o nombre…"
        className="w-full max-w-xs"
      />
      <label className="flex items-center gap-1 text-[11px] uppercase text-armazon-500">
        Desde
        <input
          type="date"
          value={desde}
          onChange={(e) => alCambiarDesde(e.target.value)}
          className={claseFecha}
        />
      </label>
      <label className="flex items-center gap-1 text-[11px] uppercase text-armazon-500">
        Hasta
        <input
          type="date"
          value={hasta}
          onChange={(e) => alCambiarHasta(e.target.value)}
          className={claseFecha}
        />
      </label>
      {alCambiarEstado && (
        <select
          value={estado}
          onChange={(e) => alCambiarEstado(e.target.value as '' | EstadoDelCertificado)}
          aria-label="Estado"
          className={`${claseFecha} uppercase`}
        >
          <option value="">Todos los estados</option>
          <option value="VIGENTE">Vigentes</option>
          <option value="VENCIDO">Vencidos</option>
          <option value="ANULADO">Anulados</option>
        </select>
      )}
    </div>
  );
}

const TONOS = {
  neutro: 'bg-armazon-100 text-armazon-600',
  marca: 'bg-marca-50 text-marca-700',
  alerta: 'bg-red-50 text-alerta',
  ambar: 'bg-amber-50 text-amber-700',
  azul: 'bg-blue-50 text-blue-700',
} as const;

function Cifra({
  titulo,
  valor,
  icono: Icono,
  tono = 'neutro',
}: {
  titulo: string;
  valor: number | undefined;
  icono: LucideIcon;
  tono?: keyof typeof TONOS;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-armazon-200 bg-white p-3 shadow-sm">
      <span className={`grid size-9 shrink-0 place-items-center rounded-lg ${TONOS[tono]}`}>
        <Icono className="size-4" aria-hidden />
      </span>
      <div className="min-w-0">
        <p className="importe text-lg font-bold leading-tight text-armazon-900">{valor ?? '—'}</p>
        <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-armazon-400">
          {titulo}
        </p>
      </div>
    </div>
  );
}

function BotonDeFila({
  icono: Icono,
  etiqueta,
  alPulsar,
  peligro = false,
}: {
  icono: LucideIcon;
  etiqueta: string;
  alPulsar: () => void;
  peligro?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={alPulsar}
      title={etiqueta}
      aria-label={etiqueta}
      className={`rounded-lg p-1.5 text-armazon-400 transition-colors ${
        peligro
          ? 'hover:bg-red-50 hover:text-alerta'
          : 'hover:bg-armazon-100 hover:text-armazon-700'
      }`}
    >
      <Icono className="size-[15px]" aria-hidden />
    </button>
  );
}

function DialogoDeDetalle({
  certificado: c,
  alCerrar,
}: {
  certificado: CertificadoEmitido;
  alCerrar: () => void;
}) {
  const detalle = useQuery({
    queryKey: ['certificados', 'detalle', c.id],
    queryFn: () => api.get<DetalleDeCertificado>(`/certificados-no-adeudar/${c.id}`),
  });
  const d = detalle.data?.certificado ?? c;

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo={d.numero}
      subtitulo={d.contribuyente}
      icono={History}
      ancho="medio"
    >
      <dl className="grid gap-x-6 gap-y-3 text-xs sm:grid-cols-2">
        <Dato nombre="Identificación" valor={d.identificacion} importe />
        <Dato nombre="Código del QR" valor={d.codigo} importe />
        <Dato nombre="Emitido" valor={fechaYHora(d.emitidoEn)} />
        <Dato nombre="Válido hasta" valor={fecha(d.validoHasta)} />
        <Dato nombre="Firmado por" valor={`${d.firmadoPor} · ${d.cargoDelFirmante}`} />
        <Dato nombre="Pedido por" valor={d.solicitadoPor ?? '—'} />
        <Dato nombre="Canal" valor={d.canal} />
        <Dato nombre="Estado" valor={d.estado} />
      </dl>

      {d.anuladoEn && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-xs uppercase text-red-900">
          <p className="font-semibold">
            Anulado el {fechaYHora(d.anuladoEn)} por {d.anuladoPor}
          </p>
          <p className="mt-1">{d.motivoDeAnulacion}</p>
        </div>
      )}

      <h3 className="mt-5 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-armazon-400">
        <ScanLine className="size-3.5" aria-hidden />
        Validaciones por QR ({d.validaciones})
      </h3>
      {detalle.data && detalle.data.validaciones.length > 0 ? (
        <ul className="mt-2 max-h-56 divide-y divide-armazon-100 overflow-y-auto rounded-lg border border-armazon-200">
          {detalle.data.validaciones.map((v) => (
            <li key={v.validadoEn} className="flex justify-between px-3 py-2 text-xs">
              <span className="text-armazon-600">{fechaYHora(v.validadoEn)}</span>
              <span className="font-semibold uppercase text-armazon-500">
                Respondió {v.resultado.toLowerCase()}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-xs uppercase text-armazon-400">
          {detalle.isPending ? 'Cargando…' : 'Nadie lo ha validado todavía.'}
        </p>
      )}
    </Modal>
  );
}

function Dato({
  nombre,
  valor,
  importe = false,
}: {
  nombre: string;
  valor: string;
  importe?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-bold uppercase tracking-wider text-armazon-400">{nombre}</dt>
      <dd
        className={`truncate text-armazon-700 ${importe ? 'importe' : 'uppercase'}`}
        title={valor}
      >
        {valor}
      </dd>
    </div>
  );
}

function DialogoDeAnulacion({
  certificado,
  alCerrar,
  alAnular,
}: {
  certificado: CertificadoEmitido;
  alCerrar: () => void;
  alAnular: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const anular = useMutation({
    mutationFn: () =>
      api.post<CertificadoEmitido>(`/certificados-no-adeudar/${certificado.id}/anular`, {
        motivo: motivo.trim(),
      }),
    onSuccess: () => {
      alAnular();
      alCerrar();
      notificar.exito(`Certificado ${certificado.numero} anulado`);
    },
  });
  const error = anular.error instanceof ErrorDeApi ? anular.error.message : null;

  return (
    <Modal abierto alCerrar={alCerrar} titulo="Anular certificado" icono={Ban} ancho="estrecho">
      <div className="space-y-3">
        <p className="rounded-lg bg-armazon-50 px-3 py-2 text-sm font-semibold uppercase text-armazon-900">
          {certificado.numero} · {certificado.contribuyente}
        </p>
        <p className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs uppercase text-amber-900">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
          No se borra: quien lo valide por el QR verá que está anulado. No se puede deshacer.
        </p>
        {error && (
          <p
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm uppercase text-red-900"
          >
            {error}
          </p>
        )}
        <Campo etiqueta="Motivo" requerido ayuda="AL MENOS 10 CARACTERES. QUEDA EN EL HISTORIAL.">
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            maxLength={300}
            className={`${claseEntrada} uppercase`}
          />
        </Campo>
      </div>
      <div className="mt-6 flex justify-end gap-2 border-t border-armazon-100 pt-5">
        <button
          type="button"
          onClick={alCerrar}
          className="rounded-lg px-4 py-2 text-sm font-semibold uppercase text-armazon-600 hover:bg-armazon-100"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => anular.mutate()}
          disabled={anular.isPending || motivo.trim().length < 10}
          className="flex items-center gap-2 rounded-lg bg-alerta px-4 py-2 text-sm font-bold uppercase text-white hover:opacity-90 disabled:opacity-50"
        >
          <Ban className="size-4" aria-hidden />
          {anular.isPending ? 'Anulando…' : 'Anular'}
        </button>
      </div>
    </Modal>
  );
}
